<template>
  <div id="customer">
    <div class="container">

      <PageTitle :title="$t('customer.customer_information')"></PageTitle>

      <Tabs>
        <TabSection :title="$t('customer.customer_information') + ' (' + customer.user.username + ')'">
          <div class="columns" v-if="customer.information">
            <div class="column">
              <InformationBlock :title="$t('customer.contact_information')" icon="/icon/user.svg" :data="[
                { label: $t('fullName'), value: fullName },
                { label: $t('phoneNumber'), value: customer.information.contact.phone },
                { label: $t('email'), value: customer.information.contact.email },
                { label: $t('language'), value: customer.information.language },
                { label: $t('validationMissing'), value: customer.user.emailValidationRequired ? $t('yes') : $t('no') }
              ]" />
            </div>
            <div class="column">
              <InformationBlock v-for="addr of customer.information.addresses" :key="`address-${addr.id}`"
                :title="$t('address.address')" icon="/icon/location.svg" :data="Object.entries(addr)
                  .sort((a1, a2) => addressFieldsOrder.indexOf(a1[0]) < addressFieldsOrder.indexOf(a2[0]) ? -1 : 1)
                  .map(([k, v]) => ({ label: $t(`address.${k}`), value: v }))" />
            </div>
          </div>
          <div v-else>
            {{ $t('customers.no_information') }}
          </div>
        </TabSection>

        <TabSection :title="$t('customer.sales')" v-if="$store.getters.loggedUserHasCompanyAdmin">
          <template v-if="$store.getters.loggedUserHasCompanyAdmin">
            <Datatable entity="sale_user" :parameters="{ user: userId }" :onRowClick="goToSale"
              :rowActions="[{ text: 'datatable.display', method: goToSale }]">
            </Datatable>
          </template>
        </TabSection>

        <TabSection :title="$t('customer.loyalty_accounts')"
          v-if="showAccounts && $store.getters.loggedUserHasCompanyAdmin">
          <template v-if="showAccounts && $store.getters.loggedUserHasCompanyAdmin">
            <Datatable entity="loyalty_account_by_customer" :parameters="{ user: userId }"
              :onRowClick="goToLoyaltyAccount" :rowActions="[{ text: 'datatable.display', method: goToLoyaltyAccount }]">
            </Datatable>
          </template>
        </TabSection>

        <TabSection :title="$t('customer.loyalty_transactions')"
          v-if="showTransactions && $store.getters.loggedUserHasCompanyAdmin">
          <template v-if="showTransactions && $store.getters.loggedUserHasCompanyAdmin">
            <Datatable entity="loyalty_transaction" :parameters="{ createdBy: userId, branch: branchId }"
              :onRowClick="goToLoyaltyTransaction"
              :rowActions="[{ text: 'datatable.display', method: goToLoyaltyTransaction }]">
            </Datatable>
          </template>
        </TabSection>

        <TabSection :title="$t('settings_admin.title')"
          v-if="isBranchAdmin && $store.getters.loggedUserHasModifyPermission">
          <template v-if="isBranchAdmin && $store.getters.loggedUserHasModifyPermission">
            <div>
              <button class="btn btn-danger" @click="popHardEditUsernameConfirmation">{{ $t('customer.hard_edit_username')
              }}</button>
            </div>
            <br />
            <div>
              <button class="btn btn-danger" @click="generateResetPasswordToken">{{
                $t('customer.generate_reset_password_token') }}</button>
              <br /><br />
              <div id="generated-reset-password-token-div" v-show="resetPasswordUrl">{{ resetPasswordUrl }}</div>
            </div>
          </template>
        </TabSection>

        <TabSection :title="$t('SuperAdmin')" v-if="isSuperAdmin">
          <Panel v-if="isSuperAdmin">
            <div v-if="adminAnalysis" class="used-ips">
              <div class="group">
                <label>{{ $t('customer.used_ips') }}</label>
                <div class="value" v-for="ip of adminAnalysis.usedIps">{{ ip }}</div>
                <div class="value" v-if="!adminAnalysis.usedIps.length">{{ $t('not_available') }}</div>
              </div>
            </div>
            <div class="hard_delete">
              <button class="btn btn-danger" @click="popHardDeleteConfirmation">{{ $t('customer.hard_delete_user')
              }}</button>
              <div class="group" style="margin-left: 12px; margin-top: 12px">
                <input id="attach_to_anonymous" type="checkbox" v-model="attachSalesToAnonymous">
                <label for="attach_to_anonymous">{{ $t("customer.on_delete_attach_sales_to_anonymous") }}</label>
              </div>
            </div>
            <div>
              <button class="btn btn-primary" @click="generateToken">{{ $t('customer.generate_login_token') }}</button>
              <div id="generated-token-div" v-show="generatedToken">{{ generatedToken }}</div>
            </div>
          </Panel>
        </TabSection>
      </Tabs>

      <Modal name="delete-confirm" dismissable :title="$t('customer.delete_confirmation_title')" :actions="[
        { name: $t('back'), action: 'close' },
        { name: $t('delete'), action: hardDelete, primary: true }
      ]">
        <p>{{ $t('customer.delete_confirmation_body') }}</p>
      </Modal>

      <Modal name="hard-edit-username" dismissable :title="$t('customer.hard_edit_username')" :actions="[
        { name: $t('back'), action: 'close' },
        { name: $t('confirm'), action: hardEditUsername, primary: true }
      ]">
        <p>{{ $t('customer.hard_edit_username') }}</p>
        <input type="text" v-model="usernameToEdit">
      </Modal>

    </div>
  </div>
</template>

<script>
  import { mapActions } from "vuex";
  import Datatable from "@/components/Datatable.vue";
  import InformationBlock from "@/components/tools/InformationBlock.vue";
  import PageTitle from "@/components/PageTitle";

  export default {
    name: "Customer",

    components: { Datatable, InformationBlock, PageTitle },

    data() {
      return {
        addressFieldsOrder: ['fullAddress', 'app', 'zip', 'city', 'state', 'country', 'lat', 'lng', 'name', 'note'],
        generatedToken: null,
        attachSalesToAnonymous: false,
        showTransactions: false,
        showAccounts: false,
        adminAnalysis: null,
        resetPasswordUrl: null,
        usernameToEdit: null,
        customer: {
          user: {
            username: '',
            branch: ''
          },
          information: {
            firstName: "",
            lastName: "",
            language: "",
            addresses: [],
            birthDate: "",
            contact: {
              email: "",
              phoneNumber: "",
              preference: "",
              messagingConsent: ""
            }
          }
        }
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "customer.title";
      this.$store.state.pageIcon = "/icon/user.svg";

      let customerId = this.$route.query.id;
      let [customer, branch, adminAnalysis] = await Promise.all([
        this.getCustomer({ id: customerId }),
        this.$store.getters.getContextBranch,
        this.isSuperAdmin ? this.getCustomerAdminAnalysis({ id: customerId }) : Promise.resolve(null)
      ]);
      if (customer) {
        this.customer = customer;
      } else {
        toast(this.$t("error.no_server"), "error");
      }
      if (branch) {
        this.showTransactions = branch.eWalletActivated || branch.loyaltyActivated;
        this.showAccounts = branch.eWalletActivated || branch.loyaltyActivated;
      }
      this.adminAnalysis = adminAnalysis;
    },

    methods: {
      ...mapActions(["getCustomer", "getCustomerAdminAnalysis", "superAdminContextLogin", "hardDeleteUser", "adminPasswordReset", "adminHardEditUsername"]),

      goToSale(row) {
        this.$router.push(this.$router.buildContext("/sale", { id: row.id }));
      },

      goToLoyaltyTransaction(row) {
        this.$router.push(this.$router.buildContext("/loyalty-transaction", { id: row.id }))
      },

      goToLoyaltyAccount(row) {
        this.$router.push(this.$router.buildContext("/loyalty-account", { id: row.id }))
      },

      async generateToken() {
        let username = this.customer.user.username;
        let branch = await this.$store.getters.getContextBranch;
        let response = await this.superAdminContextLogin({ username: username, branch: branch.id });
        if (response) {
          this.generatedToken = response.token
        } else {
          toast(this.$t("error.no_server"), "error");
        }
      },

      async hardEditUsername() {
        let username = this.customer.user.username;
        let branch = await this.$store.getters.getContextBranch;
        let response = await this.adminHardEditUsername({ username: username, newUsername: this.usernameToEdit, branch: branch.id });
        if (response) {
          if (response.user) {
            console.log('response username reset', response);
            this.customer = response;
          } else {
            toast(this.$t("error.no_server"), "error");
          }
        } else {
          toast(this.$t("error.no_server"), "error");
        }
        this.$modal.show('hard-edit-username', false)
      },

      async generateResetPasswordToken() {
        let email = this.customer.user.email;
        let branch = await this.$store.getters.getContextBranch;
        let response = await this.adminPasswordReset({ email: email, branch: branch.id });
        if (response) {
          console.log('response password reset', response);
          this.resetPasswordUrl = response.url;
        } else {
          toast(this.$t("error.no_server"), "error");
        }
        console.log('hard password reset');
      },

      popHardDeleteConfirmation() {
        this.$modal.show('delete-confirm', true)
      },

      popHardEditUsernameConfirmation() {
        this.$modal.show('hard-edit-username', true)
      },

      async hardDelete() {
        this.$modal.show('delete-confirm', false);
        let userId = this.customer.user.id;
        let response = await this.hardDeleteUser({
          id: userId,
          attachToAnonymous: this.attachSalesToAnonymous
        });
        if (response) {
          toast(this.$t("customer.deleted"), "success");
          this.$router.go(-1);
        } else {
          toast(this.$t("error.no_server"), "error");
        }
      },
    },

    computed: {
      isSuperAdmin() {
        return this.$store.getters.loggedUserHasSuperAdmin
      },
      isBranchAdmin() {
        return this.$store.getters.loggedUserHasBranchAdmin
      },
      userId() {
        return this.$route.query.id
      },
      branchId() {
        return this.$route.query.branch
      },
      fullName() {
        let str = '';
        if (this.customer.information.firstName) {
          str += this.customer.information.firstName;
          str += ' ';
        }
        if (this.customer.information.lastName) {
          str += this.customer.information.lastName;
        }
        return str;
      }
    }
  }
</script>

<style lang="scss" scoped>
  #customer {
    display: flex;
    flex-direction: column;
  }

  #generated-token-div {
    word-break: break-all;
    margin-top: 20px;
  }

  .btn.btn-danger {
    background-color: red;
    color: white;
  }

  .hard_delete {
    display: flex;
    margin-bottom: 20px;
  }

  .used-ips {
    margin-bottom: 20px;
  }
</style>